import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Loader2, RotateCcw, Trash, Mail, KeyRound, Copy, Wand2 } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, ConfirmDialog } from "@/components/admin/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/typed";
import { PageSpeed } from "@/components/admin/PageSpeed";
import { TabBar, useSavedTab } from "@/components/admin/TabBar";
import { EmailOutbox } from "@/components/admin/EmailOutbox";

const PERM_LABEL: Record<string, string> = {
  approve_orders: "Approve orders & issue codes", manage_codes: "Void / import / email codes", generate_reports: "Generate & deliver reports",
  manage_organizations: "Create & edit organizations", reveal_pii: "Reveal full SSN / license", manual_entry: "Enter applicants manually", billing: "View & send billing reports", delete_applicants: "Delete applicants",
};

function NewUserDialog({ open, onOpenChange, onSaved, perms }: { open: boolean; onOpenChange: (o: boolean) => void; onSaved: () => void; perms: string[] }) {
  const { user: me } = useAuth();
  const dowOnly = me?.role === "dow_reviewer";
  const [f, setF] = useState<any>({ email: "", name: "", role: dowOnly ? "dow_reviewer" : "oversight", password: "", permissions: [], send_welcome: true });
  const [busy, setBusy] = useState(false);
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users", f);
      if (!f.send_welcome) toast.success("User created.");
      else if (data.welcome_sent) toast.success(`User created. Sign-in instructions emailed to ${data.email}. Give them the temporary password yourself.`);
      else toast.error("User created, but the welcome email could not be sent. Use the Email button to try again.");
      onSaved();
      onOpenChange(false);
      setF({ email: "", name: "", role: dowOnly ? "dow_reviewer" : "oversight", password: "", permissions: [], send_welcome: true });
    } catch (err) {
      toast.error(errMsg(err));
    }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl" data-testid="user-form-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl">Add staff user</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <label className="block"><span className="field-label">Name</span><input required className="field-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-form-name" /></label>
          <label className="block"><span className="field-label">Email</span><input required type="email" className="field-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-form-email" /></label>
          <label className="block"><span className="field-label">Temporary password (10+ characters)</span><input required minLength={10} type="password" className="field-input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="user-form-password" /></label>
          <label className="block">
            <span className="field-label">Role</span>
            <select className="field-input" value={f.role} disabled={dowOnly} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="user-form-role">
              <option value="oversight">Oversight / research (read all, update cases)</option>
              <option value="admin">Admin (full access)</option>
              <option value="dow_reviewer">Diocese reviewer (DOW Dashboard only)</option>
            </select>
          </label>
          {f.role === "oversight" && (
            <div>
              <span className="field-label">Extra permissions</span>
              {perms.map((p) => (
                <label key={p} className="flex items-center gap-3 py-1.5 text-sm">
                  <input type="checkbox" className="h-5 w-5 accent-[#1B365D]" checked={f.permissions.includes(p)} data-testid={`user-form-perm-${p}`}
                    onChange={(e) => setF({ ...f, permissions: e.target.checked ? [...f.permissions, p] : f.permissions.filter((x: string) => x !== p) })} />
                  {PERM_LABEL[p] || p}
                </label>
              ))}
            </div>
          )}
          <label className="flex items-start gap-3 rounded-xl bg-[#F1F5FB] p-3 text-sm">
            <input type="checkbox" className="mt-0.5 h-5 w-5 accent-[#1B365D]" checked={f.send_welcome} onChange={(e) => setF({ ...f, send_welcome: e.target.checked })} data-testid="user-form-send-welcome" />
            <span><b>Email sign-in instructions</b><span className="block text-xs text-[#64748B]">Sends a link to the sign-in page and how to change their password. The temporary password is not included, so share it with them yourself.</span></span>
          </label>
          <button className="btn-primary w-full" disabled={busy} data-testid="user-form-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Create user</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const genPassword = () => {
  const a = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz", d = "23456789";
  const r = crypto.getRandomValues(new Uint32Array(12));
  const s = Array.from(r, (n, i) => (i % 4 === 3 ? d : a)[n % (i % 4 === 3 ? d.length : a.length)]).join("");
  return `${s.slice(0, 4)}-${s.slice(4, 8)}-${s.slice(8)}`;
};

function SetPasswordDialog({ u, onClose }: { u: any; onClose: () => void }) {
  const [pw, setPw] = useState(genPassword);
  const [busy, setBusy] = useState(false);
  const copy = () => navigator.clipboard.writeText(pw).then(() => toast.success("Password copied"));
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${u.id}/set-password`, { password: pw });
      toast.success(`New temporary password set for ${u.name}. Share it with them by phone or text.`);
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) { onClose(); } }}>
      <DialogContent className="max-w-md" data-testid="set-password-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl">New temporary password</DialogTitle></DialogHeader>
        <p className="text-sm text-[#64748B]">For <b className="text-[#1A2536]">{u.name}</b> ({u.email}). Their old password stops working right away.</p>
        <div className="flex gap-2">
          <input className="field-input font-mono" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="set-password-input" />
          <button type="button" className="btn-secondary btn-sm" onClick={() => setPw(genPassword())} title="Generate another" data-testid="set-password-generate"><Wand2 className="h-4 w-4" /></button>
          <button type="button" className="btn-secondary btn-sm" onClick={copy} title="Copy" data-testid="set-password-copy"><Copy className="h-4 w-4" /></button>
        </div>
        <p className="text-xs text-[#64748B]">Copy it before saving, as it won't be shown again. They can change it later under Change my password.</p>
        <button className="btn-primary w-full" disabled={busy || pw.length < 10} onClick={save} data-testid="set-password-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save new password</button>
      </DialogContent>
    </Dialog>
  );
}

function Users({ perms }: { perms: string[] }) {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [pwUser, setPwUser] = useState<any>(null);
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);
  const patch = async (id: string, body: any, msg: string) => {
    try {
      await api.patch(`/admin/users/${id}`, body);
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const sendWelcome = async (u: any) => {
    try {
      await api.post(`/admin/users/${u.id}/send-welcome`);
      toast.success(`Welcome email sent to ${u.email}. Give them the temporary password yourself (phone or text).`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const resetMfa = async (id: string) => {
    try {
      await api.post(`/admin/users/${id}/reset-mfa`);
      toast.success("MFA reset. User will re-enroll at next sign-in.");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Card title={me?.role === "dow_reviewer" ? "Diocese reviewers & compliance coordinators" : "Staff users"} testId="users-card" actions={<button className="btn-primary btn-sm" onClick={() => setOpen(true)} data-testid="user-add-button"><UserPlus className="h-4 w-4" /> {me?.role === "dow_reviewer" ? "Add coordinator" : "Add user"}</button>}>
      <div className="overflow-x-auto">
        <table className="tbl" data-testid="users-table">
          <thead><tr><th>User</th><th>Role</th><th>Permissions</th><th>MFA</th><th>Last sign-in</th><th /></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className={u.active === false ? "opacity-50" : ""}>
                <td><p className="font-semibold">{u.name}</p><p className="text-xs text-[#94A3B8]">{u.email}</p></td>
                <td>{u.role === "dow_reviewer" ? "Diocese reviewer" : <span className="capitalize">{u.role}</span>}</td>
                <td>
                  {u.role === "admin" ? <span className="text-xs text-[#64748B]">All</span> : (
                    <div className="flex flex-col gap-1">
                      {perms.map((p) => (
                        <label key={p} className="flex items-center gap-2 text-xs">
                          <input type="checkbox" className="h-4 w-4 accent-[#1B365D]" checked={(u.permissions || []).includes(p)} data-testid={`user-perm-${u.id}-${p}`}
                            onChange={(e) => patch(u.id, { permissions: e.target.checked ? [...(u.permissions || []), p] : u.permissions.filter((x: string) => x !== p) }, "Permissions updated")} />
                          {PERM_LABEL[p] || p}
                        </label>
                      ))}
                    </div>
                  )}
                </td>
                <td className="text-xs">{u.mfa_enabled ? <span className="font-semibold text-[#15803D]">Enrolled</span> : <span className="text-[#B45309]">Pending setup</span>}</td>
                <td className="text-xs">{fmtDate(u.last_login_at)}</td>
                <td>
                  {me && u.id !== me.id && (
                    <div className="flex justify-end gap-2">
                      <button className="btn-secondary btn-sm whitespace-nowrap" onClick={() => sendWelcome(u)} title={u.welcome_sent_at ? `Last sent ${fmtDate(u.welcome_sent_at)}` : "Email sign-in steps"} data-testid={`user-send-welcome-${u.id}`}><Mail className="h-4 w-4" /> {u.welcome_sent_at ? "Resend welcome" : "Welcome email"}</button>
                      <button className="btn-secondary btn-sm" onClick={() => setPwUser(u)} data-testid={`user-set-password-${u.id}`}><KeyRound className="h-4 w-4" /> Password</button>
                      <button className="btn-secondary btn-sm" onClick={() => resetMfa(u.id)} data-testid={`user-reset-mfa-${u.id}`}><RotateCcw className="h-4 w-4" /> MFA</button>
                      <button className="btn-secondary btn-sm" onClick={() => patch(u.id, { active: u.active === false }, u.active === false ? "User re-activated" : "User deactivated")} data-testid={`user-toggle-active-${u.id}`}>{u.active === false ? "Activate" : "Deactivate"}</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <NewUserDialog open={open} onOpenChange={setOpen} onSaved={load} perms={perms} />
      {pwUser && <SetPasswordDialog u={pwUser} onClose={() => setPwUser(null)} />}
    </Card>
  );
}

function DataTools() {
  const [counts, setCounts] = useState<any>(null);
  const [confirm, setConfirm] = useState("");
  const [qa, setQa] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/maintenance/test-data").then((r) => setCounts(r.data));
  useEffect(() => { load(); }, []);
  const del = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/maintenance/test-data/delete", { confirm, remove_qa_users: qa });
      toast.success(`Removed ${data.organizations} orgs, ${data.applications} applications, ${data.codes} codes, ${data.orders} orders${data.qa_users != null ? `, ${data.qa_users} QA users` : ""}`);
      setConfirm(""); load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const loadOrgs = async () => {
    try {
      const { data } = await api.post("/admin/maintenance/load-organizations");
      toast.success(data.added.length ? `Added ${data.added.length} organizations` : "All organizations already exist");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-6 grid gap-6 lg:grid-cols-2">
      <Card title="Remove test data" testId="settings-test-data-card">
        <div className="space-y-4 p-6">
          <p className="text-sm text-[#64748B]">Deletes organizations whose name starts with "TEST", plus their codes, applications and orders, and anything with a test email (.test, resend.dev, example.com). Real organizations and your account are never touched.</p>
          {counts && <p className="text-sm" data-testid="test-data-counts">Found: <b>{counts.organizations}</b> orgs, <b>{counts.applications}</b> applications, <b>{counts.codes}</b> codes, <b>{counts.orders}</b> orders, <b>{counts.qa_users}</b> QA users</p>}
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="h-4 w-4" checked={qa} onChange={(e) => setQa(e.target.checked)} data-testid="test-data-qa-checkbox" /> Also remove the QA staff logins (and stop re-creating them)</label>
          <div className="flex flex-wrap gap-3">
            <input className="field-input max-w-[160px]" placeholder='Type DELETE' value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="test-data-confirm-input" />
            <button className="btn-secondary btn-sm text-[#B91C1C]" disabled={confirm !== "DELETE" || busy} onClick={del} data-testid="test-data-delete-button"><Trash className="h-4 w-4" /> Delete test data</button>
          </div>
        </div>
      </Card>
      <Card title="Load organizations" testId="settings-load-orgs-card">
        <div className="space-y-4 p-6">
          <p className="text-sm text-[#64748B]">Adds the 14 organizations set up so far (Archmere, Diocese of Wilmington with its 81 parishes and schools, Ursuline, Serviam, and the others) with their fees, forms and application links. Organizations that already exist are skipped.</p>
          <button className="btn-primary btn-sm" onClick={loadOrgs} data-testid="load-orgs-button">Load organizations</button>
        </div>
      </Card>
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const [s, setS] = useState<any>(null);
  const [eligible, setEligible] = useState<number | null>(null);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [view, setView] = useSavedTab("settings-tab", "rules", ["rules", "users", "email", "speed", "data"]);
  const isAdmin = user && user.role === "admin";
  const isDow = user && user.role === "dow_reviewer";
  useEffect(() => {
    if (isDow) return;
    api.get("/admin/settings").then((r) => setS(r.data));
    if (isAdmin) api.get("/admin/retention/preview").then((r) => setEligible(r.data.eligible));
  }, [isAdmin, isDow]);
  if (!s) return <div className="h-40 animate-pulse rounded-2xl bg-white" />;
  if (!isAdmin) return <div><PageHeader title="Settings" /><Card><p className="p-6 text-[#64748B]">Settings are managed by the primary admin.</p></Card></div>;
  const save = async () => {
    try {
      await api.put("/admin/settings", { code_expiry_days: Number(s.code_expiry_days), retention_years: Number(s.retention_years), manual_fee: Number(s.manual_fee), partner_fee: Number(s.partner_fee), partner_name: s.partner_name, ny_adjustment: Number(s.ny_adjustment) });
      toast.success("Settings saved");
      setEligible((await api.get("/admin/retention/preview")).data.eligible);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-6">
      <PageHeader title="Settings" subtitle="Code rules, data retention and staff access." />
      <TabBar testId="settings-tabs" prefix="settings-tab" value={view} onChange={setView} tabs={[["rules", "Rules & retention"], ["users", "Staff users"], ["email", "Email outbox"], ["speed", "Page speed"], ["data", "Data tools"]]} />
      {view === "rules" && <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Code, billing & application rules" testId="settings-rules-card">
          <div className="space-y-4 p-6">
            <label className="block"><span className="field-label">New codes expire after (days)</span><input type="number" className="field-input" value={s.code_expiry_days} onChange={(e) => setS({ ...s, code_expiry_days: e.target.value })} data-testid="settings-code-expiry-input" /></label>
            <p className="text-sm text-[#64748B]">Incomplete applications lock after <b>{s.incomplete_lockout_hours} hours</b> (fixed rule).</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block"><span className="field-label">Manual-entry fee ($)</span><input type="number" className="field-input" value={s.manual_fee} onChange={(e) => setS({ ...s, manual_fee: e.target.value })} data-testid="settings-manual-fee-input" /></label>
              <label className="block"><span className="field-label">NY Adjustment ($)</span><input type="number" className="field-input" value={s.ny_adjustment} onChange={(e) => setS({ ...s, ny_adjustment: e.target.value })} data-testid="settings-ny-fee-input" /></label>
              <label className="block"><span className="field-label">Partner name</span><input className="field-input" value={s.partner_name} onChange={(e) => setS({ ...s, partner_name: e.target.value })} data-testid="settings-partner-name-input" /></label>
              <label className="block"><span className="field-label">Partner share per search ($)</span><input type="number" className="field-input" value={s.partner_fee} onChange={(e) => setS({ ...s, partner_fee: e.target.value })} data-testid="settings-partner-fee-input" /></label>
            </div>
            <button className="btn-primary btn-sm" onClick={save} data-testid="settings-save-button">Save settings</button>
          </div>
        </Card>
        <Card title="Data retention" testId="settings-retention-card">
          <div className="space-y-4 p-6">
            <label className="block"><span className="field-label">Keep applicant PII for delivered cases (years)</span><input type="number" className="field-input" value={s.retention_years} onChange={(e) => setS({ ...s, retention_years: e.target.value })} data-testid="settings-retention-input" /></label>
            <p className="text-sm text-[#64748B]">Purging removes SSN, license, DOB, contact info, addresses and signatures from delivered cases older than this, and from locked incomplete applications older than 30 days. Case history and audit logs are kept.</p>
            <div className="flex items-center gap-3">
              <button className="btn-secondary btn-sm text-[#B91C1C]" disabled={!eligible} onClick={() => setPurgeOpen(true)} data-testid="settings-purge-button"><Trash className="h-4 w-4" /> Run retention purge</button>
              <span className="text-sm text-[#64748B]" data-testid="settings-purge-eligible">{eligible ?? "…"} record(s) eligible</span>
            </div>
          </div>
        </Card>
      </div>}
      {view === "users" && <Users perms={s.permissions_catalog} />}
      {view === "email" && <EmailOutbox />}
      {view === "speed" && <PageSpeed />}
      {view === "data" && <DataTools />}
      <ConfirmDialog open={purgeOpen} onOpenChange={setPurgeOpen} danger testId="purge-dialog" title={`Purge PII from ${eligible} record(s)?`} description="This cannot be undone." confirmLabel="Purge now"
        onConfirm={async () => {
          const { data } = await api.post("/admin/retention/purge");
          toast.success(`Purged ${data.purged} record(s)`);
          setEligible(0);
        }} />
    </div>
  );
}
